import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Grid,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  TextField,
  MenuItem,
  FormControl,
  Box,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../../layout/Layout";
import { MaterialReactTable } from "material-react-table";
import { materialReceiptAPI, manageCompaniesAPI } from "../../../config";
import moment from "dayjs";

const poTypeList = [
  { label: "All", value: "All" },
  { label: "Purchase Order", value: "1" },
  { label: "Work Order", value: "2" },
];

function ProformaOrderList() {
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  const [loading, setLoading] = useState(false);
  const [proformas, setProformas] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination / Sorting / Filtering states
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState([]);
  const [filters, setFilters] = useState([]);
  const [searchString, setSearchString] = useState("");

  // Dropdown filter selections
  const [companyFilter, setCompanyFilter] = useState("All");
  const [orderTypeFilter, setOrderTypeFilter] = useState("All");

  const [companyNames, setCompanyNames] = useState([]);
  const [kpiTotals, setKpiTotals] = useState({ poCount: 0, woCount: 0, totalVal: 0 });

  // Fetch company names
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.post(manageCompaniesAPI, { intType: 3 });
      if (response.data.success) {
        setCompanyNames([{ id: "All", company_name: "All" }, ...response.data.result]);
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
    }
  }, []);

  // Fetch Proforma list
  const fetchProformas = useCallback(async () => {
    setLoading(true);
    try {
      const activeFilters = [...filters];
      if (companyFilter !== "All") {
        activeFilters.push({ id: "company_name_id", value: companyFilter });
      }
      if (orderTypeFilter !== "All") {
        activeFilters.push({ id: "order_type", value: orderTypeFilter });
      }

      const response = await axios({
        method: "POST",
        url: materialReceiptAPI,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + userData.token,
        },
        data: {
          intType: 3,
          intLimit: pageSize,
          intOffset: pageIndex * pageSize,
          txtSearchString: searchString,
          sorting: sorting,
          columnFilters: activeFilters,
        },
      });

      if (response.data.success) {
        setProformas(response.data.result);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error("Failed to fetch Material Receipts:", err);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, searchString, sorting, filters, companyFilter, orderTypeFilter, userData.token]);

  // Fetch KPI totals (without pagination limit)
  const fetchProformaTotals = useCallback(async () => {
    try {
      const activeFilters = [...filters];
      if (companyFilter !== "All") {
        activeFilters.push({ id: "company_name_id", value: companyFilter });
      }
      if (orderTypeFilter !== "All") {
        activeFilters.push({ id: "order_type", value: orderTypeFilter });
      }

      const response = await axios({
        method: "POST",
        url: materialReceiptAPI,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + userData.token,
        },
        data: {
          intType: 3,
          intLimit: 100000,
          intOffset: 0,
          txtSearchString: searchString,
          sorting: sorting,
          columnFilters: activeFilters,
        },
      });

      if (response.data.success) {
        const records = response.data.result || [];
        let poCount = 0;
        let woCount = 0;
        let totalVal = 0;
        records.forEach((row) => {
          if (row.status !== "Cancelled") {
            if (Number(row.order_type) === 1) poCount++;
            if (Number(row.order_type) === 2) woCount++;
            totalVal += Number(row.total_with_gst || 0);
          }
        });
        setKpiTotals({ poCount, woCount, totalVal });
      }
    } catch (err) {
      console.error("Failed to load material receipt totals:", err);
    }
  }, [searchString, sorting, filters, companyFilter, orderTypeFilter, userData.token]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchProformas();
    fetchProformaTotals();
  }, [fetchProformas, fetchProformaTotals]);

  const handleDeleteProforma = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this Material Receipt?");
    if (!isConfirmed) return;

    try {
      const response = await axios({
        method: "POST",
        url: materialReceiptAPI,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + userData.token,
        },
        data: {
          intType: 7,
          proformaId: id,
        },
      });

      if (response.data.success) {
        alert(response.data.message);
        fetchProformas();
        fetchProformaTotals();
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete Material Receipt.");
    }
  };

  const handleEditProforma = (id) => {
    navigate(`/material-receipt/edit/${id}`);
  };

  const handleViewProforma = (id) => {
    navigate(`/material-receipt/view/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "proforma_no",
        header: "Material Receipt No",
        size: 180,
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span
              style={{
                color: "#1976d2",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
              onClick={() => handleViewProforma(row.original.id)}
            >
              {row.original.proforma_no}
            </span>
            {row.original.is_edited === 1 && (
              <span
                style={{
                  marginLeft: "8px",
                  padding: "1px 6px",
                  fontSize: "10px",
                  backgroundColor: "#e3f2fd",
                  color: "#0d47a1",
                  borderRadius: "4px",
                  border: "1px solid #bbdefb",
                  fontWeight: "bold",
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                Edited
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "company_Name",
        header: "Company Name",
        size: 200,
      },
      {
        accessorKey: "vendor_name",
        header: "Vendor Name",
        size: 200,
      },
      {
        accessorKey: "project_name",
        header: "Project Name",
        size: 200,
      },
      {
        accessorKey: "total_with_gst",
        header: "Total Value (GST)",
        size: 150,
        Cell: ({ cell }) => `₹${parseFloat(cell.getValue() || 0).toLocaleString("en-IN")}`,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => (
          <span
            style={{
              fontWeight: "bold",
              color: cell.getValue() === "Approved" ? "green" : "orange",
            }}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Action",
        size: 150,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handleEditProforma(row.original.id)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleDeleteProforma(row.original.id)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [userData.token]
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} lg={6} md={6}>
            <Typography className="page_title" textAlign="left" variant="h5" fontWeight="bold">
              Material Receipt List
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Purchase Order</Typography>
              <Typography color="text.primary">Material Receipt List</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} style={{ display: "flex", justifyContent: "flex-end" }}>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        {/* Filters block exactly like PO List */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <FormControl fullWidth>
              <TextField
                select
                label="Order Type"
                value={orderTypeFilter}
                onChange={(e) => setOrderTypeFilter(e.target.value)}
              >
                {poTypeList.map((row, index) => (
                  <MenuItem value={row.value} key={index}>
                    {row.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={6} lg={4}>
            <FormControl fullWidth>
              <TextField
                select
                label="Company Name"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                {companyNames.map((row, index) => (
                  <MenuItem value={String(row.id)} key={index}>
                    {row.company_name}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <TextField
              label="Search"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder="Search material receipt..."
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "40px",
                  paddingLeft: "20px",
                },
                "& .MuiInputBase-input": {
                  textAlign: "center",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* KPI Cards block exactly like PO List */}
        <Grid container spacing={3} mt={2} mb={2}>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                padding: "20px",
                borderRadius: "12px",
                textAlign: "center",
                background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
                borderLeft: "6px solid #1976d2",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                Total Purchase Orders
              </Typography>
              <Typography variant="h4" color="primary.main" fontWeight={700} mt={1}>
                {kpiTotals.poCount}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                padding: "20px",
                borderRadius: "12px",
                textAlign: "center",
                background: "linear-gradient(135deg, #ede7f6 0%, #d1c4e9 100%)",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
                borderLeft: "6px solid #673ab7",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                Total Work Orders
              </Typography>
              <Typography variant="h4" color="secondary.main" fontWeight={700} mt={1}>
                {kpiTotals.woCount}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                padding: "20px",
                borderRadius: "12px",
                textAlign: "center",
                background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
                borderLeft: "6px solid #2e7d32",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                Total Order Value
              </Typography>
              <Typography variant="h4" sx={{ color: "#2e7d32" }} fontWeight={700} mt={1}>
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(kpiTotals.totalVal)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            background: "#fff",
            p: 2,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <MaterialReactTable
            columns={columns}
            data={proformas}
            enablePagination
            manualPagination
            
            rowCount={totalCount}
            onPaginationChange={(updater) => {
              const nextState =
                typeof updater === "function"
                  ? updater({ pageIndex, pageSize })
                  : updater;
              setPageIndex(nextState.pageIndex);
              setPageSize(nextState.pageSize);
            }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setFilters}
            state={{
              pageIndex,
              pageSize,
              sorting,
              columnFilters: filters,
              isLoading: loading,
            }}
            muiToolbarAlertBannerProps={
              loading
                ? {
                    color: "info",
                    children: "Loading Material Receipts...",
                  }
                : undefined
            }
          />
        </Box>
      </Grid>
    </Layout>
  );
}

export default ProformaOrderList;
